import { Component } from '@theme/component';

/**
 * Pixels subtracted from the measured header bottom when setting `--kith-header-offset`
 * (used as drawer `top`). Keep this at 0 so the drawer aligns exactly to the
 * visible bottom edge of the Kith header.
 */
const KITH_DRAWER_TOP_TRIM_PX = 0;

/**
 * Custom header component for Kith menu drawer with responsive sticky behavior
 * - Desktop: Always sticky
 * - Mobile: Sticky on scroll up only
 */
class KithHeaderComponent extends Component {
  /**
   * Whether the header has been scrolled offscreen on mobile
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {number | null} */
  #metricsRaf = null;

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {number | null} */
  #lastOffsetPx = null;

  /** @type {number | null} */
  #lastHeightPx = null;

  /**
   * Drawer `top` must match the header bar's bottom edge in the viewport.
   * Using height alone skips y-offset (e.g. content above the bar) and integer rounding can leave a 1px gap.
   */
  #updateHeaderMetrics = () => {
    const rect = this.getBoundingClientRect();
    const offsetPx = Math.max(0, rect.bottom - KITH_DRAWER_TOP_TRIM_PX);
    const heightPx = Math.round(rect.height);

    if (offsetPx === this.#lastOffsetPx && heightPx === this.#lastHeightPx) return;

    this.#lastOffsetPx = offsetPx;
    this.#lastHeightPx = heightPx;
    document.body.style.setProperty('--kith-header-offset', `${offsetPx}px`);
    document.body.style.setProperty('--kith-drawer-top', `${offsetPx}px`);
    document.body.style.setProperty('--header-height', `${heightPx}px`);
    document.body.style.setProperty('--header-group-height', `${heightPx}px`);
  };

  #scheduleHeaderMetrics = () => {
    if (this.#metricsRaf != null) return;
    this.#metricsRaf = requestAnimationFrame(() => {
      this.#metricsRaf = null;
      this.#updateHeaderMetrics();
    });
  };

  /**
   * Keeps `--header-height` and `--kith-header-offset` aligned with the live layout
   */
  #resizeObserver = new ResizeObserver(() => {
    this.#scheduleHeaderMetrics();
  });

  /**
   * Observes the header while scrolling the viewport to track when it's actively sticky
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;
      this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
    }, { threshold: 0 });

    this.#intersectionObserver.observe(this);
  };

  #handleScroll = () => {
    if (this.#scrollRaf != null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#processScroll();
    });
  };

  #processScroll = () => {
    const isDesktop = window.innerWidth > 900;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const previousStickyState = this.dataset.stickyState || '';
    const previousScrollDirection = this.dataset.scrollDirection || 'none';
    let nextStickyState = previousStickyState;
    let nextScrollDirection = previousScrollDirection;

    if (!isDesktop && !this.#offscreen) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (isDesktop) {
      nextStickyState = 'active';
      if (isScrollingUp) {
        nextScrollDirection = this.getBoundingClientRect().top >= 0 ? 'none' : 'up';
      } else {
        nextScrollDirection = 'down';
      }
    } else if (isScrollingUp) {
      this.removeAttribute('data-animating');
      if (this.getBoundingClientRect().top >= 0) {
        this.#offscreen = false;
        nextStickyState = 'inactive';
        nextScrollDirection = 'none';
      } else {
        nextStickyState = 'active';
        nextScrollDirection = 'up';
      }
    } else {
      nextScrollDirection = 'down';
    }

    if (nextStickyState !== previousStickyState) {
      this.dataset.stickyState = nextStickyState;
    }

    if (nextScrollDirection !== previousScrollDirection) {
      this.dataset.scrollDirection = nextScrollDirection;
    }

    if (nextStickyState !== previousStickyState || nextScrollDirection !== previousScrollDirection) {
      this.#scheduleHeaderMetrics();
    }

    this.#lastScrollTop = scrollTop;
  };

  #handleViewportChange = () => {
    const isDesktop = window.innerWidth > 900;
    const nextStickyState = isDesktop ? 'active' : 'inactive';

    if (this.dataset.stickyState !== nextStickyState) {
      this.dataset.stickyState = nextStickyState;
      this.dataset.scrollDirection = 'none';
    }

    this.#scheduleHeaderMetrics();
  };

  connectedCallback() {
    super.connectedCallback();
    document.body.classList.add('kith-header-mounted');
    this.#resizeObserver.observe(this);
    this.#observeStickyPosition();
    this.#scheduleHeaderMetrics();
    document.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleViewportChange, { passive: true });

    // Set initial sticky state based on screen size
    if (window.innerWidth > 900) {
      this.dataset.stickyState = 'active'; // Always sticky on desktop
    } else {
      this.dataset.stickyState = 'inactive'; // Start inactive on mobile
    }


  }



  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.classList.remove('kith-header-mounted');
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    if (this.#metricsRaf != null) {
      cancelAnimationFrame(this.#metricsRaf);
      this.#metricsRaf = null;
    }
    if (this.#scrollRaf != null) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }
    document.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleViewportChange);
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--header-group-height', '0px');
    document.body.style.setProperty('--kith-header-offset', '0px');
    document.body.style.removeProperty('--kith-drawer-top');
  }
}

customElements.define('kith-header-component', KithHeaderComponent); 